import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { CourseItem, GetClassroomByTeacher } from '@edtech-ui/core/models/Classroom/get-classroomByTeacher';
import { ClassroomManagementService } from '@edtech-ui/core/services/classroom-management-service/classroom-management.service';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { ToastrService } from 'ngx-toastr';
import { PaginatorModule } from 'primeng/paginator';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-courses',
  standalone: true,
  imports: [PaginatorModule, FormsModule, CommonModule],
  templateUrl: './courses.component.html',
  styleUrl: './courses.component.scss'
})
export class CoursesComponent {
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private classroomService: ClassroomManagementService,
    private courseManagement: CourseManagementService,
    private router: Router,
    private toastr: ToastrService
  ) { }

  classrooms: GetClassroomByTeacher[] = [];
  courses: CourseItem[] = [];
  searchText = '';
  selectedClassroom: number | 'all' = 'all';
  selectedStatus: string | 'all' = 'all';
  page = 1;
  limit = 10;
  totalRecords = 0;
  isLoading = false;
  courseStatusEnum = CourseStatusEnum;

  ngOnInit(): void {
    this.loadClassrooms();
    this.initializeSearch();
    this.loadCourses();
  }

  private initializeSearch() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.page = 1;
        this.loadCourses();
      });
  }

  searchCourse(value: string) {
    this.searchSubject.next(value);
  }

  loadClassrooms() {
    this.classroomService
      .getAllTeacherClassrooms('all', 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.Success && res.Data?.classrooms) {
            this.classrooms = res.Data.classrooms;
          } else {
            this.toastr.error('Unable to load classrooms', 'Error');
          }
        },
        error: () => {
          this.toastr.error('Failed to load classrooms', 'Server Error');
        },
      });
  }

  loadCourses() {
    this.classroomService
      .getCourses({
        page: this.page,
        page_size: this.limit,
        search: this.searchText || null,
        status_filter: this.selectedStatus === 'all' ? null : this.selectedStatus,
        classroom_filter: this.selectedClassroom === 'all' ? null : Number(this.selectedClassroom),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.Success) {
            this.courses = res.Data.courses || [];
            this.totalRecords = res.Data.total_count ?? 0;

            if (this.courses.length === 0) {
              this.toastr.info('No courses found for selected filters', 'No Data');
            }
          } else {
            this.toastr.error(res.Message || 'Failed to load courses', 'Error');
          }
        },
        error: () => {
          this.toastr.error('Failed to fetch courses from server', 'Server Error');
        },
      });
  }

  getSelectedClassroomName(): string {
    const classroom = this.classrooms.find(c => c.id === this.selectedClassroom);
    return classroom ? classroom.name : '';
  }

  viewCourse(course: CourseItem) {
    const courseId = course.course_id;
    const classroom = this.classrooms.find(c => c.name === course.classroom);
    const classroomId = classroom?.id;

    if (!courseId || !classroomId) {
      this.toastr.error('Invalid course or classroom details', 'Navigation Error');
      return;
    }

    this.courseManagement.getCoursesByCourseId(courseId).subscribe({
      next: (res) => {
        if (!res?.Success || !res.Data) {
          this.toastr.error('Failed to load course details', 'Error');
          return;
        }

        const courseDetails = res.Data;

        if (courseDetails.is_ai) {
          this.router.navigate(['/teacher-dashboard/ai-course-result', classroomId, courseId]);
        } else {
          this.router.navigate([
            '/teacher-dashboard',
            classroomId,
            'manual-course-creation',
            'course-preview'
          ], { queryParams: { courseId } });
        }
      },
      error: () => {
        this.toastr.error('Server error while loading course');
      },
    });
  }

  onPageChange(event: any) {
    this.page = event.page + 1;
    this.limit = event.rows;
    this.loadCourses();
  }

  clearClassroom() {
    this.selectedClassroom = 'all';
    this.applyFilter();
  }

  clearStatus() {
    this.selectedStatus = 'all';
    this.applyFilter();
  }

  applyFilter() {
    this.page = 1;
    this.loadCourses();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

